import { useClerk, useOrganization, useUser } from '@clerk/shared/react';
import { eventFlowStepMounted } from '@clerk/shared/telemetry';
import type { OrganizationDomainResource } from '@clerk/shared/types';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  CheckboxInput,
  Col,
  descriptors,
  Flex,
  Flow,
  Icon,
  localizationKeys,
  Spinner,
  Text,
  useLocalizations,
} from '@/customizables';
import { Alert } from '@/elements/Alert';
import { Animated } from '@/elements/Animated';
import { ClipboardInput } from '@/elements/ClipboardInput';
import { useCardState } from '@/elements/contexts';
import { Field } from '@/elements/FieldControl';
import { Form } from '@/elements/Form';
import { Tooltip } from '@/elements/Tooltip';
import { Checkmark, Clipboard, Close, RotateLeftRight } from '@/icons';
import { common } from '@/styledSystem';
import { useFormControl } from '@/ui/utils/useFormControl';
import { getFieldError, getGlobalError } from '@/utils/errorHandler';

import { useConfigureSSO } from '../ConfigureSSOContext';
import { areConnectionDomainsReady, isOrganizationDomainVerified } from '../domain/organizationEnterpriseConnection';
import { Step } from '../elements/Step';
import { useWizard } from '../elements/Wizard/WizardContext';
import { RemoveDomainDialog } from '../RemoveDomainDialog';

export const OrganizationDomainsStep = (): JSX.Element => {
  const { t } = useLocalizations();
  const {
    enterpriseConnection,
    connectionDomains,
    setConnectionDomains,
    claimedDomains,
    organizationDomains,
    organizationEnterpriseConnection,
    contentRef,
    organizationDomainMutations: { createDomain, revalidate, prepareOwnershipVerification },
  } = useConfigureSSO();
  const { goPrev, goNext, isFirstStep, isLastStep } = useWizard();
  const card = useCardState();
  const clerk = useClerk();
  const { organization } = useOrganization();
  const [domainToRemove, setDomainToRemove] = useState<OrganizationDomainResource | null>(null);
  const [isUpdatingDomains, setIsUpdatingDomains] = useState(false);

  const hasRecordedTelemetryEvent = useRef(false);
  useEffect(() => {
    if (hasRecordedTelemetryEvent.current) {
      return;
    }

    hasRecordedTelemetryEvent.current = true;
    clerk.telemetry?.record(
      eventFlowStepMounted('configureSSO', 'verify-domain', {
        timestamp: new Date().toISOString(),
        connectionStatus: organizationEnterpriseConnection.status,
        connectionId: enterpriseConnection?.id ?? null,
        organizationId: organization?.id ?? null,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateDomain = async (domain: string) => {
    card.setError(undefined);

    try {
      await createDomain(domain);
    } catch (err: any) {
      const apiError = getFieldError(err) ?? getGlobalError(err);
      card.setError(apiError);
    }
  };

  const handlePrepareOwnershipVerification = async (domain: OrganizationDomainResource) => {
    card.setError(undefined);

    try {
      await prepareOwnershipVerification([domain]);
    } catch (err: any) {
      const apiError = getFieldError(err) ?? getGlobalError(err);
      card.setError(apiError);
    }
  };

  const handleToggleDomain = async (domain: OrganizationDomainResource, checked: boolean) => {
    if (isUpdatingDomains) {
      return;
    }

    card.setError(undefined);
    setIsUpdatingDomains(true);

    const domains = checked
      ? [...connectionDomains, domain.name]
      : connectionDomains.filter(name => name !== domain.name);

    try {
      await setConnectionDomains(domains);
    } catch (err: any) {
      const apiError = getFieldError(err) ?? getGlobalError(err);
      card.setError(apiError);
    } finally {
      setIsUpdatingDomains(false);
    }
  };

  const handleRemoveDomain = async (domain: OrganizationDomainResource) => {
    if (connectionDomains.includes(domain.name)) {
      await setConnectionDomains(connectionDomains.filter(name => name !== domain.name));
    }

    await domain.delete();
    await revalidate();
  };

  const domainsReady = areConnectionDomainsReady(connectionDomains, organizationDomains, claimedDomains);

  // An existing connection must keep at least one domain, so its last one can
  // be neither deselected nor removed.
  const lockLastConnectionDomain = Boolean(enterpriseConnection) && connectionDomains.length === 1;
  const lastConnectionDomainTooltip = enterpriseConnection?.active
    ? localizationKeys('configureSSO.organizationDomainsStep.domainCard.removeButtonTooltip__lastVerifiedDomainActive')
    : localizationKeys('configureSSO.organizationDomainsStep.domainCard.removeButtonTooltip__lastVerifiedDomain');

  return (
    <Flow.Part part='organizationDomains'>
      <Step
        elementDescriptor={descriptors.configureSSOStep}
        elementId={descriptors.configureSSOStep.setId('verify-domain')}
      >
        <Step.Header
          title={t(localizationKeys('configureSSO.organizationDomainsStep.title'))}
          description={t(localizationKeys('configureSSO.organizationDomainsStep.subtitle'))}
        />

        <Step.Body>
          <Step.Section
            fill
            sx={t => ({ gap: t.space.$5, minHeight: 0 })}
          >
            <Col sx={t => ({ gap: t.space.$4 })}>
              <DomainsField
                onSubmit={handleCreateDomain}
                organizationDomains={organizationDomains}
              />

              {!organizationDomains?.length && <DomainSuggestion onSubmit={handleCreateDomain} />}

              {card.error && (
                <Alert
                  variant='danger'
                  title={card.error}
                />
              )}
            </Col>

            {!!organizationDomains?.length && (
              <Col
                elementDescriptor={descriptors.configureSSOVerifyDomainList}
                sx={t => ({
                  gap: t.space.$3,
                  flex: '0 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  // Inset so card shadows/focus rings are not clipped by the
                  // scroll container's overflow.
                  marginInline: `calc(${t.space.$1} * -1)`,
                  paddingInline: t.space.$1,
                  ...common.unstyledScrollbar(t),
                })}
              >
                {organizationDomains.map(domain => {
                  const isSelected = connectionDomains.includes(domain.name);
                  const isLocked = isSelected && lockLastConnectionDomain;
                  return (
                    <DomainCard
                      key={domain.id}
                      domain={domain}
                      isSelected={isSelected}
                      claimedBy={claimedDomains.get(domain.name)}
                      onToggle={checked => void handleToggleDomain(domain, checked)}
                      isToggleDisabled={isLocked || isUpdatingDomains}
                      onRemove={() => setDomainToRemove(domain)}
                      onPrepareOwnershipVerification={() => handlePrepareOwnershipVerification(domain)}
                      isRemoveDisabled={isLocked}
                      removeDisabledTooltip={lastConnectionDomainTooltip}
                    />
                  );
                })}
              </Col>
            )}
          </Step.Section>
        </Step.Body>

        <Step.Footer>
          <Step.Footer.Previous
            onClick={() => goPrev()}
            isDisabled={isFirstStep}
          />
          <Step.Footer.Continue
            onClick={() => goNext()}
            isDisabled={isLastStep || !domainsReady}
          />
        </Step.Footer>
      </Step>

      {domainToRemove && (
        <RemoveDomainDialog
          isOpen={!!domainToRemove}
          onClose={() => setDomainToRemove(null)}
          domain={domainToRemove.name}
          isConnectionActive={Boolean(enterpriseConnection?.active)}
          onRemove={() => handleRemoveDomain(domainToRemove)}
          contentRef={contentRef}
        />
      )}
    </Flow.Part>
  );
};

/**
 * Matches a bare domain such as `example.com` or `sub.example.co.uk`.
 * Each label must start and end with an alphanumeric character and a valid
 * TLD of at least two letters is required. Protocols, paths, ports, spaces
 * and single-label hostnames are rejected.
 */
const DOMAIN_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const isValidDomain = (value: string): boolean => DOMAIN_REGEX.test(value);

const DomainsField = ({
  onSubmit,
  organizationDomains,
}: {
  onSubmit: (domain: string) => Promise<void>;
  organizationDomains: OrganizationDomainResource[] | undefined;
}): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const domainField = useFormControl('domain', '', {
    type: 'text',
    label: localizationKeys('configureSSO.organizationDomainsStep.formFieldLabel__domain'),
    placeholder: localizationKeys('configureSSO.organizationDomainsStep.formFieldInputPlaceholder__domain'),
  });

  const domain = domainField.value.trim().toLowerCase();
  const canSubmit = isValidDomain(domain) && !organizationDomains?.some(d => d.name === domain) && !isSubmitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    void onSubmit(domain)
      .then(() => domainField.setValue(''))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Form.Root onSubmit={handleSubmit}>
      <Field.Root {...domainField.props}>
        <Col
          elementDescriptor={descriptors.formField}
          elementId={descriptors.formField.setId(domainField.id)}
          sx={t => ({ gap: t.space.$2 })}
        >
          <Field.LabelRow>
            <Field.Label />
          </Field.LabelRow>

          <Flex
            align='start'
            sx={t => ({ gap: t.space.$2 })}
          >
            <Box sx={{ flex: 1 }}>
              <Field.Input />
            </Box>

            <Button
              type='submit'
              variant='bordered'
              colorScheme='secondary'
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
              localizationKey={localizationKeys('configureSSO.organizationDomainsStep.formButtonPrimary__add')}
              sx={{ flexShrink: 0 }}
            />
          </Flex>
        </Col>
      </Field.Root>
    </Form.Root>
  );
};

const getEmailDomain = (email: string): string | null => email.split('@')[1]?.trim().toLowerCase() || null;

const DomainSuggestion = ({ onSubmit }: { onSubmit: (domain: string) => Promise<void> }): JSX.Element | null => {
  const { user } = useUser();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const primaryEmail = user?.primaryEmailAddress?.emailAddress;
  const domain = primaryEmail ? getEmailDomain(primaryEmail) : null;

  if (!domain || isDismissed) {
    return null;
  }

  const handleAdd = () => {
    setIsSubmitting(true);
    void onSubmit(domain)
      .then(() => setIsDismissed(true))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Flex
      elementDescriptor={descriptors.configureSSOVerifyDomainSuggestion}
      align='center'
      justify='between'
      sx={t => ({
        gap: t.space.$2,
        paddingInline: t.space.$3,
        paddingBlock: t.space.$1x5,
        borderWidth: t.borderWidths.$normal,
        borderStyle: t.borderStyles.$solid,
        borderColor: t.colors.$borderAlpha150,
        borderRadius: t.radii.$lg,
        background: t.colors.$neutralAlpha50,
      })}
    >
      <Flex
        align='center'
        sx={t => ({ gap: t.space.$3, minWidth: 0 })}
      >
        <Text
          as='span'
          colorScheme='secondary'
          localizationKey={localizationKeys('configureSSO.organizationDomainsStep.domainSuggestion.messageLabel', {
            domain,
          })}
          sx={t => ({ fontSize: t.fontSizes.$sm })}
        />

        <Button
          variant='bordered'
          colorScheme='secondary'
          size='xs'
          isLoading={isSubmitting}
          onClick={handleAdd}
          localizationKey={localizationKeys(
            'configureSSO.organizationDomainsStep.domainSuggestion.formButtonPrimary__add',
            { domain },
          )}
          sx={{ flexShrink: 0 }}
        />
      </Flex>

      <Button
        variant='ghost'
        colorScheme='neutral'
        aria-label='Dismiss domain suggestion'
        isDisabled={isSubmitting}
        onClick={() => setIsDismissed(true)}
        sx={t => ({ flexShrink: 0, padding: t.space.$1 })}
      >
        <Icon
          icon={Close}
          sx={t => ({ width: t.sizes.$4, height: t.sizes.$4, color: t.colors.$colorMutedForeground })}
        />
      </Button>
    </Flex>
  );
};

const DomainCard = ({
  domain,
  isSelected,
  claimedBy,
  onToggle,
  isToggleDisabled = false,
  onRemove,
  onPrepareOwnershipVerification,
  isRemoveDisabled = false,
  removeDisabledTooltip,
}: {
  domain: OrganizationDomainResource;
  /** Whether the scoped connection authenticates this domain. */
  isSelected: boolean;
  /** The name of the other connection that already authenticates this domain. */
  claimedBy: string | undefined;
  onToggle: (checked: boolean) => void;
  isToggleDisabled?: boolean;
  onRemove: () => void;
  onPrepareOwnershipVerification: () => Promise<void>;
  isRemoveDisabled?: boolean;
  removeDisabledTooltip?: ReturnType<typeof localizationKeys>;
}): JSX.Element | null => {
  const { t } = useLocalizations();

  if (!domain.name) {
    return null;
  }

  const ownershipVerification = domain.ownershipVerification;
  const isVerified = isOrganizationDomainVerified(domain);
  const isExpired = ownershipVerification?.status === 'expired';
  const cardId = ownershipVerification?.status ?? 'unverified';
  // Only a verified domain no other connection claims can join this one.
  const isSelectable = isVerified && (!claimedBy || isSelected);

  const removeButton = (
    <Button
      elementDescriptor={descriptors.configureSSOVerifyDomainCardRemoveButton}
      variant='ghost'
      colorScheme='neutral'
      aria-label='Remove domain'
      onClick={onRemove}
      isDisabled={isRemoveDisabled}
      sx={t => ({ flexShrink: 0, padding: t.space.$1 })}
    >
      <Icon
        icon={Close}
        sx={t => ({ width: t.sizes.$4, height: t.sizes.$4, color: t.colors.$colorMutedForeground })}
      />
    </Button>
  );

  return (
    <Col
      elementDescriptor={descriptors.configureSSOVerifyDomainCard}
      elementId={descriptors.configureSSOVerifyDomainCard.setId(cardId)}
      sx={t => ({
        borderWidth: t.borderWidths.$normal,
        borderStyle: t.borderStyles.$solid,
        borderColor: t.colors.$borderAlpha150,
        borderRadius: t.radii.$lg,
        background: t.colors.$colorBackground,
      })}
    >
      <Flex
        align='center'
        justify='between'
        sx={t => ({ gap: t.space.$2, padding: t.space.$4, paddingBottom: t.space.$2 })}
      >
        <Flex
          align='center'
          sx={t => ({ gap: t.space.$2, minWidth: 0 })}
        >
          <CheckboxInput
            elementDescriptor={descriptors.configureSSOVerifyDomainCardCheckbox}
            checked={isSelected}
            isDisabled={!isSelectable || isToggleDisabled}
            aria-label={t(
              localizationKeys('configureSSO.organizationDomainsStep.domainCard.checkboxLabel', {
                domain: domain.name,
              }),
            )}
            onChange={e => onToggle(e.target.checked)}
          />

          <Text
            as='span'
            sx={t => ({
              fontWeight: t.fontWeights.$medium,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            })}
          >
            {domain.name}
          </Text>

          <Badge
            elementDescriptor={descriptors.configureSSOVerifyDomainCardBadge}
            elementId={descriptors.configureSSOVerifyDomainCardBadge.setId(cardId)}
            colorScheme={isVerified ? 'success' : 'danger'}
            localizationKey={
              isVerified
                ? localizationKeys('configureSSO.organizationDomainsStep.domainCard.badge__verified')
                : isExpired
                  ? localizationKeys('configureSSO.organizationDomainsStep.domainCard.badge__expired')
                  : localizationKeys('configureSSO.organizationDomainsStep.domainCard.badge__unverified')
            }
          />

          {claimedBy && (
            <Badge
              elementDescriptor={descriptors.configureSSOVerifyDomainCardBadge}
              elementId={descriptors.configureSSOVerifyDomainCardBadge.setId('claimed')}
              colorScheme='primary'
              localizationKey={localizationKeys('configureSSO.organizationDomainsStep.domainCard.badge__claimed')}
            />
          )}

          {!isVerified && !isExpired && (
            <Spinner
              size='xs'
              colorScheme='neutral'
              sx={t => ({ flexShrink: 0, marginInlineStart: t.space.$1 })}
            />
          )}
        </Flex>

        {isRemoveDisabled && removeDisabledTooltip ? (
          <Tooltip.Root>
            <Tooltip.Trigger>{removeButton}</Tooltip.Trigger>
            <Tooltip.Content text={removeDisabledTooltip} />
          </Tooltip.Root>
        ) : (
          removeButton
        )}
      </Flex>

      <Box sx={{ overflow: 'hidden' }}>
        <Animated>
          {isExpired ? (
            <ExpiredNotice
              key='expired'
              expiresAt={ownershipVerification?.expiresAt ?? null}
              onPrepareOwnershipVerification={onPrepareOwnershipVerification}
            />
          ) : ownershipVerification?.verifiedAt ? (
            <Text
              key='verified'
              as='p'
              colorScheme='secondary'
              localizationKey={localizationKeys('configureSSO.organizationDomainsStep.domainCard.verifiedAtLabel', {
                date: ownershipVerification.verifiedAt,
              })}
              sx={t => ({ padding: t.space.$4, paddingTop: 0 })}
            />
          ) : (
            <TxtRecord
              key='unverified'
              ownershipVerification={ownershipVerification}
            />
          )}
        </Animated>
      </Box>
    </Col>
  );
};

const ExpiredNotice = ({
  expiresAt,
  onPrepareOwnershipVerification,
}: {
  expiresAt: Date | null;
  onPrepareOwnershipVerification: () => Promise<void>;
}): JSX.Element => {
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerifyAgain = () => {
    setIsVerifying(true);
    void onPrepareOwnershipVerification().finally(() => setIsVerifying(false));
  };

  return (
    <Col
      elementDescriptor={descriptors.configureSSOVerifyDomainCardExpired}
      sx={t => ({ gap: t.space.$3, paddingInline: t.space.$4, paddingBottom: t.space.$4 })}
    >
      <Text
        as='p'
        colorScheme='secondary'
        localizationKey={
          expiresAt
            ? localizationKeys('configureSSO.organizationDomainsStep.domainCard.expiredAtLabel', { date: expiresAt })
            : localizationKeys('configureSSO.organizationDomainsStep.domainCard.expiredLabel')
        }
      />

      <Button
        variant='bordered'
        colorScheme='secondary'
        size='xs'
        isLoading={isVerifying}
        onClick={handleVerifyAgain}
        sx={t => ({ alignSelf: 'flex-start', gap: t.space.$1x5 })}
      >
        <Icon
          icon={RotateLeftRight}
          size='sm'
          colorScheme='neutral'
        />
        <Text
          as='span'
          localizationKey={localizationKeys('configureSSO.organizationDomainsStep.domainCard.verifyAgainButton')}
        />
      </Button>
    </Col>
  );
};

const TxtRecord = ({
  ownershipVerification,
}: {
  ownershipVerification: OrganizationDomainResource['ownershipVerification'];
}): JSX.Element => {
  return (
    <Col
      elementDescriptor={descriptors.configureSSOVerifyDomainCardTxtRecord}
      sx={t => ({ gap: t.space.$3, paddingInline: t.space.$4, paddingBottom: t.space.$4 })}
    >
      <Text
        as='p'
        colorScheme='secondary'
        localizationKey={localizationKeys('configureSSO.organizationDomainsStep.domainCard.txtRecord.instructions')}
        sx={t => ({ fontSize: t.fontSizes.$sm })}
      />

      <Box
        sx={t => ({
          borderTopWidth: t.borderWidths.$normal,
          borderTopStyle: t.borderStyles.$solid,
          borderTopColor: t.colors.$borderAlpha100,
          marginInline: `calc(${t.space.$4} * -1)`,
        })}
      />

      <Flex
        wrap='wrap'
        sx={t => ({ gap: t.space.$6 })}
      >
        <RecordEntry
          label={localizationKeys('configureSSO.organizationDomainsStep.domainCard.txtRecord.typeLabel')}
          value='TXT'
        />
        <RecordEntry
          label={localizationKeys('configureSSO.organizationDomainsStep.domainCard.txtRecord.hostLabel')}
          value={ownershipVerification?.txtRecordName ?? '@'}
        />
      </Flex>

      <Flex
        align='center'
        sx={t => ({ gap: t.space.$2, minWidth: 0 })}
      >
        <Text
          as='span'
          colorScheme='secondary'
          localizationKey={localizationKeys('configureSSO.organizationDomainsStep.domainCard.txtRecord.valueLabel')}
          sx={t => ({ fontSize: t.fontSizes.$sm, flexShrink: 0 })}
        />
        <ClipboardInput
          elementDescriptor={descriptors.configureSSOVerifyDomainCardTxtRecordValue}
          value={ownershipVerification?.txtRecordValue ?? '—'}
          copyIcon={Clipboard}
          copiedIcon={Checkmark}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Flex>
    </Col>
  );
};

const RecordEntry = ({ label, value }: { label: ReturnType<typeof localizationKeys>; value: string }): JSX.Element => {
  return (
    <Flex
      align='center'
      sx={t => ({ gap: t.space.$2, minWidth: 0 })}
    >
      <Text
        as='span'
        colorScheme='secondary'
        localizationKey={label}
        sx={t => ({ fontSize: t.fontSizes.$sm, flexShrink: 0 })}
      />
      <Badge
        colorScheme='primary'
        sx={t => ({
          fontFamily: t.fonts.$buttons,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBlock: t.space.$1,
          paddingInline: t.space.$1x5,
        })}
      >
        <Box
          as='span'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            textBoxTrim: 'trim-both',
            textBoxEdge: 'cap alphabetic',
          }}
        >
          {value}
        </Box>
      </Badge>
    </Flex>
  );
};
